import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, CalendarDays, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import api from "../lib/api";
import { formatDate as formatDateCR } from "../lib/date";

const typeLabels = {
  mandatory: "Obligatorio",
  optional: "Opcional",
};
const typeColors = {
  mandatory: "bg-red-50 text-red-700 border-red-200",
  optional: "bg-amber-50 text-amber-700 border-amber-200",
};
const paymentLabels = {
  double_pay: "Pago doble",
  day_off: "Dia libre",
  optional_double: "Doble opcional",
};

const currentYear = new Date().getFullYear();

const emptyForm = {
  name: "",
  date: "",
  year: currentYear,
  holiday_type: "mandatory",
  payment_rule: "double_pay",
  notes: "",
};

export default function Holidays({ embedded = false }) {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filterYear, setFilterYear] = useState(currentYear);
  const [seeding, setSeeding] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get("/holidays", {
        params: { year: filterYear },
      });
      setHolidays(data);
    } catch {
      toast.error("Error al cargar feriados");
    } finally {
      setLoading(false);
    }
  }, [filterYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    setEditId(null);
    setForm({ ...emptyForm, year: filterYear });
    setDialogOpen(true);
  };

  const openEdit = (h) => {
    setEditId(h.id);
    setForm({
      name: h.name,
      date: h.date,
      year: h.year,
      holiday_type: h.holiday_type,
      payment_rule: h.payment_rule,
      notes: h.notes || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.date) {
      toast.error("Nombre y fecha son requeridos");
      return;
    }
    try {
      const payload = {
        ...form,
        year: Number(form.year) || filterYear,
      };
      if (editId) {
        await api.put(`/holidays/${editId}`, payload);
        toast.success("Feriado actualizado");
      } else {
        await api.post("/holidays", payload);
        toast.success("Feriado creado");
      }
      setDialogOpen(false);
      fetchData();
    } catch {
      toast.error("Error al guardar feriado");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este feriado?")) return;
    try {
      await api.delete(`/holidays/${id}`);
      toast.success("Feriado eliminado");
      fetchData();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post(`/holidays/seed/${filterYear}`);
      toast.success(data.detail);
      if (data.note) toast(data.note, { duration: 6000 });
      fetchData();
    } catch {
      toast.error("Error al generar feriados");
    } finally {
      setSeeding(false);
    }
  };

  const formatDate = (d) => formatDateCR(d);

  const years = [];
  for (let y = currentYear - 1; y <= currentYear + 2; y++) years.push(y);

  return (
    <div data-testid="holidays-page">
      <div className="flex items-center justify-between mb-6">
        {!embedded && (
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
              Dias feriados
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Feriados oficiales por ano segun legislacion laboral
            </p>
          </div>
        )}
        {embedded && <div />}
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSeed}
            disabled={seeding}
            data-testid="seed-holidays-button"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {seeding ? "Generando..." : `Cargar feriados CR ${filterYear}`}
          </Button>
          <Button
            onClick={openCreate}
            className="bg-zinc-900 text-white hover:bg-zinc-800"
            data-testid="add-holiday-button"
          >
            <Plus className="w-4 h-4 mr-2" /> Nuevo feriado
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-6">
        <Select
          value={String(filterYear)}
          onValueChange={(v) => setFilterYear(Number(v))}
        >
          <SelectTrigger className="w-[140px]" data-testid="holiday-filter-year">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg">
          Jueves Santo y Viernes Santo deben agregarse manualmente (fechas variables)
        </p>
      </div>

      <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-100 hover:bg-transparent">
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Fecha
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Nombre
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Tipo
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden sm:table-cell">
                  Regla de pago
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden md:table-cell">
                  Notas
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right">
                  Acciones
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {holidays.map((h) => (
                <TableRow
                  key={h.id}
                  className="border-zinc-100"
                  data-testid={`holiday-row-${h.id}`}
                >
                  <TableCell className="text-sm text-zinc-600 font-mono">
                    {formatDate(h.date)}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-900 font-medium">
                    {h.name}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`font-medium ${typeColors[h.holiday_type] || ""}`}
                    >
                      {typeLabels[h.holiday_type] || h.holiday_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 hidden sm:table-cell">
                    {paymentLabels[h.payment_rule] || h.payment_rule}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-400 hidden md:table-cell max-w-[200px] truncate">
                    {h.notes || "\u2014"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(h)}
                      data-testid={`edit-holiday-${h.id}`}
                    >
                      <Pencil className="w-4 h-4 text-zinc-400" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(h.id)}
                      data-testid={`delete-holiday-${h.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {holidays.length === 0 && !loading && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-16 text-zinc-400"
                  >
                    <CalendarDays className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">No hay feriados para {filterYear}</p>
                    <p className="text-xs text-zinc-400 mt-1">
                      Use "Cargar feriados CR" para pre-cargar los feriados oficiales
                    </p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">
              {editId ? "Editar feriado" : "Nuevo feriado"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-xs font-medium text-zinc-700">
                Nombre *
              </Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Dia de la Independencia"
                data-testid="holiday-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Fecha *
                </Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => {
                    const d = e.target.value;
                    setForm({
                      ...form,
                      date: d,
                      year: d ? Number(d.split("-")[0]) : form.year,
                    });
                  }}
                  data-testid="holiday-date-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Tipo
                </Label>
                <Select
                  value={form.holiday_type}
                  onValueChange={(v) => setForm({ ...form, holiday_type: v })}
                >
                  <SelectTrigger data-testid="holiday-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mandatory">Obligatorio (de ley)</SelectItem>
                    <SelectItem value="optional">Opcional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-zinc-700">
                Regla de pago
              </Label>
              <Select
                value={form.payment_rule}
                onValueChange={(v) => setForm({ ...form, payment_rule: v })}
              >
                <SelectTrigger data-testid="holiday-payment-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="double_pay">Pago doble (si trabaja)</SelectItem>
                  <SelectItem value="day_off">Dia libre (no se trabaja)</SelectItem>
                  <SelectItem value="optional_double">Doble opcional</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-zinc-700">
                Notas
              </Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Decreto N.o..."
                data-testid="holiday-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="holiday-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="holiday-submit-button"
            >
              {editId ? "Actualizar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
